import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { Loader2, Flag, Trophy, TrendingUp, Target, CalendarDays, ChevronLeft, Medal, Zap, Award, Bot, Send } from "lucide-react";

const STATUS_STYLE = {
  "IN QUALIFICATION ZONE": "border-green-500/60 text-green-400 bg-green-500/10",
  "ON THE BUBBLE": "border-amber-500/60 text-amber-400 bg-amber-500/10",
  "OUTSIDE QUALIFICATION ZONE": "border-red-500/60 text-red-400 bg-red-500/10",
  "NOT YET RANKED": "border-zinc-700 text-zinc-500 bg-zinc-800/40",
};
const statusStyle = (s) => (s || "").startsWith("QUALIFIED") ? "border-[#BEF264]/70 text-[#BEF264] bg-[#BEF264]/10" : STATUS_STYLE[s] || STATUS_STYLE["NOT YET RANKED"];

const QUICK_QS = ["How close am I to Nationals?", "What tournament should I play next?", "Who is immediately ahead of me?", "Which results count toward my Best 8?"];

function RankingCoach() {
  const [q, setQ] = useState("");
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setBusy(true);
    setQ("");
    setThread((t) => [...t, { role: "you", text }]);
    try {
      const { data } = await api.post("/pbr/coach", { question: text });
      setThread((t) => [...t, { role: "coach", text: data.answer }]);
    } catch (e) {
      setThread((t) => [...t, { role: "coach", text: e.response?.data?.detail || "The coach is unavailable right now — try again shortly." }]);
    }
    setBusy(false);
  };
  return (
    <div className="border border-pb-cyan/40 bg-pb-cyan/5 p-4 mb-8" data-testid="ranking-coach">
      <div className="text-[9px] uppercase tracking-[0.3em] text-pb-cyan font-bold mb-2 flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> Ranking Coach — answers from your real PBR data</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {QUICK_QS.map((qq) => (
          <button key={qq} data-testid={`coach-quick-${QUICK_QS.indexOf(qq)}`} disabled={busy} onClick={() => ask(qq)}
            className="text-[10px] border border-pb-cyan/30 text-zinc-400 px-2.5 py-1 hover:text-pb-cyan hover:border-pb-cyan/60 transition-colors disabled:opacity-40">{qq}</button>
        ))}
      </div>
      {thread.length > 0 && (
        <div className="space-y-2 mb-3 max-h-64 overflow-y-auto" data-testid="coach-thread">
          {thread.map((m, i) => (
            <div key={i} className={`text-xs leading-relaxed ${m.role === "you" ? "text-zinc-500" : "text-zinc-200"}`}>
              <span className={`font-black uppercase text-[9px] tracking-widest mr-2 ${m.role === "you" ? "text-zinc-600" : "text-pb-cyan"}`}>{m.role === "you" ? "You" : "Coach"}</span>
              {m.text}
            </div>
          ))}
          {busy && <div className="text-xs text-pb-cyan flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Coach is checking your numbers…</div>}
        </div>
      )}
      <div className="flex gap-2">
        <input data-testid="coach-input" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Ask about your ranking, points, or path to Nationals…"
          className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white focus:outline-none focus:border-pb-cyan" />
        <button data-testid="coach-send" disabled={busy || !q.trim()} onClick={() => ask()}
          className="border border-pb-cyan/60 text-pb-cyan px-4 py-2 hover:bg-pb-cyan hover:text-black transition-colors disabled:opacity-40"><Send className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}
const LEVEL_COLOR = { local: "text-zinc-300", district: "text-pb-cyan", regional: "text-amber-400", state: "text-pb-red", national: "text-[#BEF264]" };

export default function RoadToNationals() {
  const { pid } = useParams();
  const { realUser } = useAuth();
  const navigate = useNavigate();
  const playerId = pid || realUser?.id;
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!playerId) return;
    api.get(`/pbr/player/${playerId}`).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [playerId]);

  if (d === null) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-pb-red" /></div>;
  if (d === false) return <div className="text-center py-24 text-zinc-500" data-testid="rtn-error">Couldn't load Road to Nationals.</div>;
  const primary = d.disciplines[0];

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="road-to-nationals-page">
      <button data-testid="rtn-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mb-4 transition-colors">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div className="flex flex-wrap items-center gap-4 mb-2">
        <Avatar src={d.avatar} name={d.name} size={56} testId="rtn-avatar" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-pb-red font-bold flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Road to Nationals · {d.season} Season</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase leading-none" data-testid="rtn-player-name">{d.name}</h1>
        </div>
        <button data-testid="rtn-view-rankings" onClick={() => navigate("/pbr-rankings")}
          className="ml-auto text-[10px] uppercase tracking-widest font-bold border border-pb-cyan/50 text-pb-cyan px-4 py-2 hover:bg-pb-cyan hover:text-black transition-colors">
          National Rankings →
        </button>
      </div>
      <p className="text-xs text-zinc-500 mb-8">Local → District → Regional → State → National. Every sanctioned tournament earns PBR points — performance beats participation.</p>

      {/* Ranking Coach — only on your own page */}
      {(!pid || pid === realUser?.id) && <RankingCoach />}

      {!primary ? (
        <div className="border border-pb-border bg-pb-surface p-10 text-center" data-testid="rtn-empty">
          <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="font-display font-black text-xl uppercase mb-1">No PBR points yet</div>
          <p className="text-sm text-zinc-500 mb-4 max-w-md mx-auto">Play a PBR-sanctioned tournament to establish a ranking. Champions earn up to 100 pts at Locals, 250 at Districts, 500 at Regionals, 1,000 at States and 2,000 at Nationals.</p>
          <button data-testid="rtn-find-events" onClick={() => navigate("/tournaments")}
            className="text-[11px] uppercase tracking-widest font-bold bg-pb-red text-black px-6 py-2.5 hover:bg-[#BEF264] transition-colors">Find a Sanctioned Event</button>
        </div>
      ) : (
        <>
          {/* Hero: where do I stand */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="rtn-hero">
            <div className="border border-pb-red/40 bg-pb-surface p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">PBR 52 Rank · {primary.discipline}</div>
              <div className="font-display font-black text-4xl text-pb-red" data-testid="rtn-rank">#{primary.rank}</div>
              <div className="text-[10px] text-zinc-600">of {primary.field_size} ranked</div>
            </div>
            <div className="border border-pb-border bg-pb-surface p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">PBR Points (Best {d.best_n})</div>
              <div className="font-display font-black text-4xl text-white" data-testid="rtn-points">{primary.points}</div>
              <div className="text-[10px] text-zinc-600">Race: #{primary.race_rank || "—"} · {primary.race_points} pts this season</div>
            </div>
            <div className="border border-pb-border bg-pb-surface p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">National Qualification</div>
              <div className={`inline-block border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${STATUS_STYLE[primary.status]}`} data-testid="rtn-status">{primary.status}</div>
              <div className="text-[10px] text-zinc-600 mt-1.5">Top {d.qual_size} qualify per discipline/division</div>
            </div>
            <div className="border border-pb-border bg-pb-surface p-4">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1 flex items-center gap-1"><Target className="w-3 h-3" /> Points to Qualification</div>
              <div className="font-display font-black text-4xl text-pb-cyan" data-testid="rtn-points-needed">{primary.points_to_cutoff || 0}</div>
              <div className="text-[10px] text-zinc-600">{primary.points_to_cutoff ? `~${primary.points_to_cutoff} pts to reach the current zone` : "You're inside the current zone"}</div>
            </div>
          </div>

          {/* Next best opportunity */}
          {d.opportunities.length > 0 && (
            <div className="border border-[#BEF264]/40 bg-[#BEF264]/5 p-4 mb-8" data-testid="rtn-opportunity">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#BEF264] font-bold mb-2 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Your Next Big Opportunity</div>
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <div className="font-display font-black text-xl uppercase">{d.opportunities[0].name}</div>
                  <div className="text-xs text-zinc-400">{d.opportunities[0].level_label} · {d.opportunities[0].date} · up to <span className="text-[#BEF264] font-bold">{d.opportunities[0].max_points} pts</span>{d.opportunity_impact ? ` · Impact: ${d.opportunity_impact}` : ""}</div>
                </div>
                <button data-testid="rtn-opportunity-view" onClick={() => navigate(`/tournaments`)}
                  className="ml-auto text-[10px] uppercase tracking-widest font-bold border border-[#BEF264]/60 text-[#BEF264] px-4 py-2 hover:bg-[#BEF264] hover:text-black transition-colors">Register</button>
              </div>
            </div>
          )}

          {/* Achievements */}
          {d.achievements?.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2 flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Achievements</div>
              <div className="flex flex-wrap gap-2 mb-8" data-testid="rtn-achievements">
                {d.achievements.map((a) => (
                  <div key={a.badge} className="border border-yellow-500/40 bg-yellow-500/5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-yellow-400" title={a.source || ""}>
                    🏅 {a.badge}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Discipline rankings */}
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">Discipline Rankings</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="rtn-disciplines">
            {d.disciplines.map((dc) => (
              <div key={dc.discipline} className="border border-pb-border bg-pb-surface p-4" data-testid={`rtn-disc-${dc.discipline.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-black uppercase">{dc.discipline}</div>
                  <div className="font-display font-black text-2xl text-pb-red">#{dc.rank || "—"}</div>
                </div>
                <div className={`inline-block border px-2 py-0.5 text-[9px] font-black uppercase tracking-wider mb-2 ${statusStyle(dc.status)}`}>{dc.status}</div>
                <div className="text-xs text-zinc-400">{dc.points} pts (best {d.best_n}) · Race #{dc.race_rank || "—"}</div>
                {dc.best.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {dc.best.slice(0, 4).map((b) => (
                      <div key={b.id} className={`flex items-center gap-2 text-[10px] ${b.counted ? "text-zinc-300" : "text-zinc-600 line-through"}`}>
                        <span className={`font-bold ${LEVEL_COLOR[b.level] || "text-zinc-400"}`}>{b.points}</span>
                        <span className="truncate">{b.tournament_name}</span>
                        <span className="ml-auto shrink-0">{b.finish === 1 ? "🥇" : b.finish === 2 ? "🥈" : b.finish === 3 ? "🥉" : `#${b.finish}`}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Season performance */}
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">{d.season} Season Performance</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-8 text-center" data-testid="rtn-season-stats">
            {[["Events", d.season_stats.events], ["Titles", d.season_stats.championships], ["Podiums", d.season_stats.podiums],
              ["Match W", d.season_stats.match_wins], ["Match L", d.season_stats.match_losses],
              ["Win %", d.season_stats.win_pct != null ? `${d.season_stats.win_pct}%` : "—"]].map(([l, v]) => (
              <div key={l} className="border border-pb-border bg-pb-surface p-3">
                <div className="font-display font-black text-xl">{v}</div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
              </div>
            ))}
          </div>

          {/* Movement */}
          {d.movement && d.movement.rank_before && d.movement.rank_after && d.movement.rank_before !== d.movement.rank_after && (
            <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-3 mb-8 flex items-center gap-3 text-sm" data-testid="rtn-movement">
              <TrendingUp className="w-4 h-4 text-pb-cyan" />
              <span className="text-zinc-300">Latest move ({d.movement.tournament_name}): <span className="font-bold text-white">#{d.movement.rank_before} → #{d.movement.rank_after}</span>
                <span className={d.movement.rank_before > d.movement.rank_after ? "text-green-400 font-bold" : "text-red-400 font-bold"}> ({d.movement.rank_before > d.movement.rank_after ? "+" : "−"}{Math.abs(d.movement.rank_before - d.movement.rank_after)} positions)</span></span>
            </div>
          )}

          {/* History */}
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2 flex items-center gap-1.5"><Medal className="w-3.5 h-3.5" /> Tournament History</div>
          <div className="border border-pb-border bg-pb-surface divide-y divide-pb-border/60" data-testid="rtn-history">
            {d.history.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-xs">
                <span className="text-zinc-500 shrink-0 flex items-center gap-1"><CalendarDays className="w-3 h-3" />{h.date}</span>
                <span className="text-white font-semibold truncate">{h.tournament_name}</span>
                <span className={`uppercase text-[9px] font-bold tracking-wider ${LEVEL_COLOR[h.level] || "text-zinc-400"}`}>{h.level_label}</span>
                <span className="text-zinc-400">{h.discipline}{h.division ? ` · ${h.division}` : ""}</span>
                <span className="ml-auto shrink-0 text-zinc-400">{h.finish === 1 ? "🥇 Champion" : h.finish === 2 ? "🥈 Finalist" : `#${h.finish}`} · {h.wins}–{h.losses}</span>
                <span className="shrink-0 font-display font-black text-[#BEF264]">+{h.points} pts</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-600 mt-4">{d.note}</p>
        </>
      )}
    </div>
  );
}
